// Sugalarm — Time-in-Range strip. 5 segments colored by zone, widths
// proportional to the percentage values from the analytics summary.
//
// Server-side rounding already drifts to total exactly 100.0 (cycle 6),
// so we trust the values and only normalize defensively when all are
// zero (empty window).

import type { TIRBuckets } from "../../lib/types";
import styles from "./TIRBar.module.css";

interface Props {
  tir: TIRBuckets;
  height?: number;
  showLegend?: boolean;
}

// `color` is the canonical zone fill used for the BAR segments (held
// verbatim across every surface). Cycle 169: `textColor` is the LEGEND
// value's ink — it prefers the darker `--zone-*-text` shade (defined
// only on the bare mobile-Light `:root`) and falls back to the canonical
// zone color everywhere else (mobile Dark, all six web/kiosk themes), so
// only small zone-colored text on the light ground darkens for contrast.
const SEGS: Array<{
  key: keyof TIRBuckets;
  color: string;
  textColor: string;
  short: string;
}> = [
  {
    key: "urgent_low_pct",
    color: "var(--zone-urgent)",
    textColor: "var(--zone-urgent-text, var(--zone-urgent))",
    short: "ul",
  },
  {
    key: "low_pct",
    color: "var(--zone-warn)",
    textColor: "var(--zone-warn-text, var(--zone-warn))",
    short: "low",
  },
  {
    key: "in_range_pct",
    color: "var(--zone-ok)",
    textColor: "var(--zone-ok-text, var(--zone-ok))",
    short: "in",
  },
  {
    key: "high_pct",
    color: "var(--zone-warn)",
    textColor: "var(--zone-warn-text, var(--zone-warn))",
    short: "high",
  },
  {
    key: "urgent_high_pct",
    color: "var(--zone-urgent)",
    textColor: "var(--zone-urgent-text, var(--zone-urgent))",
    short: "uh",
  },
];

export function TIRBar({ tir, height = 16, showLegend = true }: Props) {
  const total =
    tir.urgent_low_pct +
    tir.low_pct +
    tir.in_range_pct +
    tir.high_pct +
    tir.urgent_high_pct;

  // Empty window — render an empty rounded track instead of a 0-width bar.
  if (total <= 0) {
    return (
      <div>
        <div className={styles.bar} style={{ height }} />
        {showLegend && (
          <div className={styles.legend}>
            <span>No readings in window</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className={styles.bar} style={{ height }}>
        {SEGS.map((s) => {
          const pct = Number(tir[s.key]) || 0;
          if (pct <= 0) return null;
          return (
            <span
              key={s.key}
              className={styles.seg}
              style={{ width: `${pct}%`, background: s.color }}
              aria-label={`${s.short} ${pct.toFixed(1)}%`}
            />
          );
        })}
      </div>
      {showLegend && (
        <div className={styles.legend}>
          {SEGS.map((s) => (
            <span key={s.key} className={styles.legendItem}>
              <span
                className={styles.legendValue}
                style={{ color: s.textColor }}
              >
                {(Number(tir[s.key]) || 0).toFixed(0)}%
              </span>
              <span>{s.short}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
